/**
 * Global status banners, mounted once in App.jsx:
 *
 * 1. Secure-data-session banner — listens for the supabase bridge degraded /
 *    recovered events from src/utils/supabaseToken.js. While degraded, every
 *    Supabase query 401s (sentinel token), so pages show error states; this
 *    banner tells the user what is actually wrong and offers a reload.
 *
 * 2. Update banner — polls /version.json (stamped by scripts/
 *    write-version-json.js at build time) every few minutes and on tab focus.
 *    When a new deploy lands, long-lived tabs get a "reload" nudge instead of
 *    running stale code forever (the 2026-11-03 "empty deals page" incident:
 *    pre-RLS-bridge bundles kept querying anonymously after the security flip
 *    and saw zero rows).
 *
 * Both banners are deliberately plain fixed-position elements — no portal or
 * router dependency, safe to mount above every provider.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  CloseButton,
  HStack,
} from '@chakra-ui/react';
import {
  BRIDGE_DEGRADED_EVENT,
  BRIDGE_RECOVERED_EVENT,
  isRlsBridgeEnabled,
} from '../utils/supabaseToken';

const VERSION_POLL_MS = 5 * 60 * 1000; // background cadence; focus also checks

async function fetchBuildId() {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.buildId || null;
  } catch {
    return null; // offline/transient — never surface version noise
  }
}

export default function AppStatusBanners() {
  const [bridgeDown, setBridgeDown] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const bootBuildIdRef = useRef(null);

  // --- bridge degraded/recovered ---
  useEffect(() => {
    if (!isRlsBridgeEnabled()) return undefined;
    const onDown = () => setBridgeDown(true);
    const onUp = () => setBridgeDown(false);
    window.addEventListener(BRIDGE_DEGRADED_EVENT, onDown);
    window.addEventListener(BRIDGE_RECOVERED_EVENT, onUp);
    return () => {
      window.removeEventListener(BRIDGE_DEGRADED_EVENT, onDown);
      window.removeEventListener(BRIDGE_RECOVERED_EVENT, onUp);
    };
  }, []);

  // --- new-version detection ---
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const check = async () => {
      const liveId = await fetchBuildId();
      if (cancelled || !liveId) return;
      if (!bootBuildIdRef.current) {
        // First successful fetch after boot = the version we are running.
        bootBuildIdRef.current = liveId;
        return;
      }
      if (liveId !== bootBuildIdRef.current) {
        setUpdateReady(true);
      }
    };

    const onFocus = () => { check(); };

    check();
    timer = setInterval(check, VERSION_POLL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  const showUpdate = updateReady && !updateDismissed;
  if (!bridgeDown && !showUpdate) return null;

  return (
    <Box position="fixed" top={0} left={0} right={0} zIndex={2000}>
      {bridgeDown && (
        <Alert status="error" variant="solid" justifyContent="center" py={2}>
          <AlertIcon />
          <AlertTitle fontSize="sm">Secure data connection lost.</AlertTitle>
          <AlertDescription fontSize="sm" mr={3}>
            Your data is safe, but nothing can load until it reconnects — retrying automatically.
          </AlertDescription>
          <Button size="xs" colorScheme="whiteAlpha" onClick={() => window.location.reload()}>
            Reload now
          </Button>
        </Alert>
      )}
      {showUpdate && (
        <Alert status="info" variant="solid" justifyContent="center" py={2}>
          <AlertIcon />
          <AlertTitle fontSize="sm">A new version of the Hub is available.</AlertTitle>
          <AlertDescription fontSize="sm" mr={3}>
            Reload to pick up the latest fixes — unsaved work in open forms will be lost.
          </AlertDescription>
          <HStack spacing={2}>
            <Button size="xs" colorScheme="whiteAlpha" onClick={() => window.location.reload()}>
              Reload now
            </Button>
            <CloseButton size="sm" onClick={() => setUpdateDismissed(true)} />
          </HStack>
        </Alert>
      )}
    </Box>
  );
}
